import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Alert,
  Button,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { sectorsAPI } from '../services/api';
import {
  SectorsTable,
  SectorDetailsDialog,
  SectorsManagementSkeleton,
} from '../components/sectors-management';

function SectorsManagement() {
  const { t } = useTranslation();
  const [sectors, setSectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSector, setSelectedSector] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [sectorDetails, setSectorDetails] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchSectors();
  }, []);

  const fetchSectors = async () => {
    try {
      setLoading(true);
      const response = await sectorsAPI.getManagement();
      setSectors(response.data.sectors || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching sectors:', err);
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchSectors();
    setRefreshing(false);
  };

  const handleViewDetails = async (sectorCode) => {
    setSelectedSector(sectorCode);
    setDetailLoading(true);
    setSectorDetails(null);
    
    try {
      const response = await sectorsAPI.getDetails(sectorCode);
      setSectorDetails(response.data);
    } catch (err) {
      console.error('Error fetching sector details:', err);
      setError(err.response?.data?.detail || err.message);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleCloseDetails = () => {
    setSelectedSector(null);
    setSectorDetails(null);
    setDetailLoading(false);
  };

  const handleRefreshSector = async (sectorCode) => {
    try {
      const response = await sectorsAPI.refresh(sectorCode);
      if (response.data.success) {
        alert(t('sectorsManagement.messages.refreshSuccess', { code: sectorCode }));
        fetchSectors();
      } else {
        alert(t('sectorsManagement.messages.refreshFailed', { message: response.data.message }));
      }
    } catch (err) {
      console.error('Error refreshing sector:', err);
      alert(t('sectorsManagement.messages.refreshError', { error: err.response?.data?.detail || err.message }));
    }
  };


  if (loading) {
    return <SectorsManagementSkeleton />;
  }

  if (error) {
    return (
      <Box sx={{ px: 0, py: { xs: 1, sm: 2 } }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ px: 0, py: { xs: 1, sm: 2 } }}>
      {/* Header */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
            {t('sectorsManagement.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            {t('sectorsManagement.subtitle')}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
          onClick={handleRefresh}
          disabled={refreshing}
          sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8rem' }}
        >
          {refreshing ? t('sectorsManagement.refreshing') : t('sectorsManagement.refresh')}
        </Button>
      </Box>

      {/* Sectors Table */}
      <SectorsTable
        sectors={sectors}
        onViewDetails={handleViewDetails}
        onRefreshSector={handleRefreshSector}
      />

      {/* Detail Dialog */}
      <SectorDetailsDialog
        open={Boolean(selectedSector)}
        onClose={handleCloseDetails}
        loading={detailLoading}
        sectorCode={selectedSector}
        sectorDetails={sectorDetails}
      />
    </Box>
  );
}

export default SectorsManagement;

